import React, { useState, useEffect } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { db } from '../../../../Services/firebase-config';
import { useAuth } from '../../../../Contexts/AuthContext';
import {
    ArrowLeftIcon,
    CheckCircleIcon,
    ClockIcon,
    DocumentIcon,
    CodeIcon,
    ExternalLinkIcon,
    InformationCircleIcon,
    StarIcon,
    DownloadIcon
} from '@heroicons/react/outline';

const ApplicationDetails = () => {
    const { applicationId } = useParams();
    const location = useLocation();
    const navigate = useNavigate();
    const { userData } = useAuth();
    const [application, setApplication] = useState(location.state?.application || null);
    const [loading, setLoading] = useState(!application);
    const [updating, setUpdating] = useState(false);
    const [successMessage, setSuccessMessage] = useState('');
    const [evaluationForm, setEvaluationForm] = useState({
        technical: 0,
        innovation: 0,
        feasibility: 0,
        feedback: '',
        overallScore: 0
    });

    const handleEvaluationFormChange = (field, value) => {
        setEvaluationForm(prev => {
            const updated = { ...prev, [field]: value };

            // Calculate overall score when criteria scores change
            if (field === 'technical' || field === 'innovation' || field === 'feasibility') {
                const totalCriteriaScore =
                    (field === 'technical' ? value : updated.technical) +
                    (field === 'innovation' ? value : updated.innovation) +
                    (field === 'feasibility' ? value : updated.feasibility);
                updated.overallScore = Math.round((totalCriteriaScore / 30) * 100);
            }

            return updated;
        });
    };

    const handleDownloadPdf = () => {
        if (application?.pitchDeckUrl) {
            const link = document.createElement('a');
            link.href = application.pitchDeckUrl;
            link.download = application.pitchDeckFileName || 'pitch-deck.pdf';
            link.target = '_blank';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        }
    };

    // Extract YouTube video ID from URL
    const getYouTubeVideoId = (url) => {
        const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
        const match = url?.match(regExp);
        return (match && match[2].length === 11) ? match[2] : null;
    };

    useEffect(() => {
        if (!application && applicationId) {
            fetchApplication();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [applicationId, application]);

    const fetchApplication = async () => {
        try {
            setLoading(true);
            const docRef = doc(db, 'hackathonApplications', applicationId);
            const docSnap = await getDoc(docRef);

            if (docSnap.exists()) {
                setApplication({ id: docSnap.id, ...docSnap.data() });
            } else {
                console.error('Application not found');
            }
        } catch (error) {
            console.error('Error fetching application:', error);
        } finally {
            setLoading(false);
        }
    };

    const evaluateApplication = async () => {
        if (!application || application.isEvaluated) return;

        if (!canEvaluate()) {
            console.error('User does not have permission to evaluate applications');
            return;
        }

        try {
            setUpdating(true);
            const evaluationData = {
                score: evaluationForm.overallScore,
                criteria: {
                    technical: evaluationForm.technical,
                    innovation: evaluationForm.innovation,
                    feasibility: evaluationForm.feasibility
                },
                feedback: evaluationForm.feedback,
                evaluator: userData?.displayName || userData?.email || 'Unknown',
                evaluatedAt: new Date()
            };

            const docRef = doc(db, 'hackathonApplications', application.id);
            await updateDoc(docRef, {
                isEvaluated: true,
                evaluation: evaluationData,
                evaluatedAt: new Date(),
                evaluatedBy: userData?.email || 'Unknown',
                updatedAt: new Date()
            });

            setApplication(prev => ({
                ...prev,
                isEvaluated: true,
                evaluation: evaluationData
            }));

            setSuccessMessage('Application evaluated successfully');
            setTimeout(() => setSuccessMessage(''), 3000);
        } catch (error) {
            console.error('Error evaluating application:', error);
        } finally {
            setUpdating(false);
        }
    };

    const canEvaluate = () => {
        return userData && (userData.role === 'Judge' || userData.role === 'Administrator');
    };

    const getStatusBadge = (app) => {
        if (app.isEvaluated) {
            return (
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-purple-100 text-purple-800">
                    <DocumentIcon className="w-4 h-4 mr-1" />
                    Evaluated
                </span>
            );
        }
        return (
            <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-yellow-100 text-yellow-800">
                <ClockIcon className="w-4 h-4 mr-1" />
                Pending Evaluation
            </span>
        );
    };

    const formatDate = (timestamp) => {
        if (!timestamp) return 'N/A';

        let date;
        if (timestamp.toDate) {
            date = timestamp.toDate();
        } else if (timestamp.seconds) {
            date = new Date(timestamp.seconds * 1000);
        } else if (typeof timestamp === 'number') {
            date = new Date(timestamp);
        } else {
            date = new Date(timestamp);
        }

        if (isNaN(date.getTime())) {
            return 'Invalid Date';
        }

        return date.toLocaleDateString('en-GB', {
            day: 'numeric',
            month: 'short',
            year: 'numeric'
        });
    };

    if (loading) {
        return (
            <div className="flex justify-center items-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-custom-blue-1"></div>
            </div>
        );
    }

    if (!application) {
        return (
            <div className="min-h-screen bg-gray-50 p-6">
                <div className="text-center">
                    <h1 className="text-2xl font-bold text-gray-900 mb-2">Application Not Found</h1>
                    <button
                        onClick={() => navigate('/eventdashboard/manage-applications')}
                        className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-custom-blue-1 hover:bg-blue-700"
                    >
                        <ArrowLeftIcon className="w-4 h-4 mr-2" />
                        Back to Applications
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50 p-6">
            <div className="max-w-7xl mx-auto">
                {/* Header */}
                <div className="mb-6">
                    <button
                        onClick={() => navigate(-1)}
                        className="inline-flex items-center text-custom-blue-1 hover:text-blue-700 mb-4"
                    >
                        <ArrowLeftIcon className="w-4 h-4 mr-1" />
                        Back
                    </button>
                    <div className="flex justify-between items-start">
                        <div>
                            <h1 className="text-3xl font-bold text-gray-900">
                                {application.teamName || 'Application Details'}
                            </h1>
                            <p className="text-gray-600 mt-1">
                                Submitted on {formatDate(application.submissionDate)}
                            </p>
                        </div>
                        {getStatusBadge(application)}
                    </div>
                </div>

                {/* Success Message */}
                {successMessage && (
                    <div className="mb-6 bg-green-50 border border-green-200 rounded-md p-4">
                        <div className="flex">
                            <CheckCircleIcon className="h-5 w-5 text-green-400" />
                            <div className="ml-3">
                                <p className="text-sm font-medium text-green-800">
                                    {successMessage}
                                </p>
                            </div>
                        </div>
                    </div>
                )}

                {/* Row 1: Evaluation Form (Left) and YouTube Video (Right) */}
                {canEvaluate() && !application.isEvaluated && (
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
                        {/* Left: Evaluation Form */}
                        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                            <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2 mb-6">
                                <StarIcon className="w-5 h-5 text-purple-600" />
                                Evaluate Application
                            </h2>

                            {/* Overall Score Display */}
                            <div className="p-4 bg-purple-50 rounded-lg mb-6">
                                <div className="flex items-center justify-between mb-2">
                                    <h4 className="font-semibold text-gray-700">Overall Score</h4>
                                    <span className="text-2xl font-bold text-purple-600">
                                        {evaluationForm.overallScore}/100
                                    </span>
                                </div>
                                <div className="w-full bg-gray-200 rounded-full h-3">
                                    <div
                                        className="bg-purple-600 h-3 rounded-full transition-all duration-300"
                                        style={{ width: `${evaluationForm.overallScore}%` }}
                                    ></div>
                                </div>
                                <p className="text-sm text-gray-600 mt-2">
                                    Automatically calculated based on criteria scores
                                </p>
                            </div>

                            {/* Criteria Scores */}
                            <div className="space-y-6 mb-6">
                                <h4 className="font-semibold text-gray-700">Evaluation Criteria (out of 10)</h4>

                                {/* Technical Skills */}
                                <div>
                                    <div className="flex items-center justify-between mb-2">
                                        <label className="text-sm font-medium text-gray-700">
                                            Technical Skills
                                        </label>
                                        <span className="text-lg font-semibold text-purple-600">
                                            {evaluationForm.technical}/10
                                        </span>
                                    </div>
                                    <input
                                        type="range"
                                        min="0"
                                        max="10"
                                        value={evaluationForm.technical}
                                        onChange={(e) => handleEvaluationFormChange('technical', parseInt(e.target.value))}
                                        className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-purple-600"
                                    />
                                    <div className="flex justify-between text-xs text-gray-500 mt-1">
                                        <span>Poor</span>
                                        <span>Excellent</span>
                                    </div>
                                </div>

                                {/* Innovation */}
                                <div>
                                    <div className="flex items-center justify-between mb-2">
                                        <label className="text-sm font-medium text-gray-700">
                                            Innovation & Creativity
                                        </label>
                                        <span className="text-lg font-semibold text-purple-600">
                                            {evaluationForm.innovation}/10
                                        </span>
                                    </div>
                                    <input
                                        type="range"
                                        min="0"
                                        max="10"
                                        value={evaluationForm.innovation}
                                        onChange={(e) => handleEvaluationFormChange('innovation', parseInt(e.target.value))}
                                        className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-purple-600"
                                    />
                                    <div className="flex justify-between text-xs text-gray-500 mt-1">
                                        <span>Low</span>
                                        <span>High</span>
                                    </div>
                                </div>

                                {/* Feasibility */}
                                <div>
                                    <div className="flex items-center justify-between mb-2">
                                        <label className="text-sm font-medium text-gray-700">
                                            Project Feasibility
                                        </label>
                                        <span className="text-lg font-semibold text-purple-600">
                                            {evaluationForm.feasibility}/10
                                        </span>
                                    </div>
                                    <input
                                        type="range"
                                        min="0"
                                        max="10"
                                        value={evaluationForm.feasibility}
                                        onChange={(e) => handleEvaluationFormChange('feasibility', parseInt(e.target.value))}
                                        className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-purple-600"
                                    />
                                    <div className="flex justify-between text-xs text-gray-500 mt-1">
                                        <span>Unrealistic</span>
                                        <span>Very Feasible</span>
                                    </div>
                                </div>
                            </div>

                            {/* Feedback */}
                            <div className="mb-6">
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Evaluator Feedback
                                </label>
                                <textarea
                                    value={evaluationForm.feedback}
                                    onChange={(e) => handleEvaluationFormChange('feedback', e.target.value)}
                                    rows={6}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-purple-500 focus:border-purple-500 resize-none"
                                    placeholder="Provide detailed feedback about the application..."
                                />
                            </div>

                            {/* Submit Button */}
                            <button
                                onClick={evaluateApplication}
                                disabled={updating || !evaluationForm.feedback.trim()}
                                className="w-full px-6 py-3 bg-purple-600 text-white rounded-md hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-purple-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                            >
                                {updating ? (
                                    <>
                                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                                        Submitting...
                                    </>
                                ) : (
                                    <>
                                        <CheckCircleIcon className="w-5 h-5" />
                                        Submit Evaluation
                                    </>
                                )}
                            </button>
                        </div>

                        {/* Right: YouTube Video */}
                        {application.youtubePitchDeck && (
                            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                                <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2 mb-4">
                                    <ExternalLinkIcon className="h-5 w-5 text-custom-blue-1" />
                                    YouTube Pitch Video
                                </h2>

                                {getYouTubeVideoId(application.youtubePitchDeck) ? (
                                    <div className="aspect-video rounded-lg overflow-hidden">
                                        <iframe
                                            width="100%"
                                            height="100%"
                                            src={`https://www.youtube.com/embed/${getYouTubeVideoId(application.youtubePitchDeck)}`}
                                            title="YouTube Pitch Video"
                                            frameBorder="0"
                                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                            allowFullScreen
                                        ></iframe>
                                    </div>
                                ) : (
                                    <div className="p-6 text-center bg-gray-50 rounded-lg">
                                        <p className="text-gray-500 mb-4">Unable to embed video. Invalid YouTube URL.</p>
                                        <a
                                            href={application.youtubePitchDeck}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="inline-flex items-center text-custom-blue-1 hover:text-blue-700"
                                        >
                                            <ExternalLinkIcon className="h-4 w-4 mr-1" />
                                            Open YouTube Video
                                        </a>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                )}

                {/* Row 2: PDF Viewer */}
                {application.pitchDeckUrl && (
                    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
                        <div className="flex items-center justify-between mb-4">
                            <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                                <DocumentIcon className="h-5 w-5 text-custom-blue-1" />
                                Pitch Deck (PDF)
                            </h2>
                            <button
                                onClick={handleDownloadPdf}
                                className="flex items-center gap-2 px-4 py-2 bg-custom-blue-1 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm"
                            >
                                <DownloadIcon className="w-4 h-4" />
                                Download PDF
                            </button>
                        </div>
                        
                        <div className="bg-gray-50 rounded-lg border overflow-hidden" style={{ height: '800px' }}>
                            <iframe
                                src={`${application.pitchDeckUrl}#toolbar=1&navpanes=1&scrollbar=1`}
                                width="100%"
                                height="100%"
                                title="Pitch Deck PDF"
                                className="border-0"
                            />
                        </div>
                    </div>
                )}

                {/* Other Application Details */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8">
                    {/* Team Section */}
                    <div className="mb-8">
                        <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2 mb-4">
                            <InformationCircleIcon className="h-5 w-5 text-custom-blue-1" />
                            Team Information
                        </h2>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                            <div>
                                <p className="text-xs uppercase tracking-wider text-gray-500 font-medium">Team Name</p>
                                <p className="mt-1 text-sm text-gray-900 font-semibold">{application.teamName || 'N/A'}</p>
                            </div>
                            <div>
                                <p className="text-xs uppercase tracking-wider text-gray-500 font-medium">Team Size</p>
                                <p className="mt-1 text-sm text-gray-900">{application.teamSize || 'N/A'}</p>
                            </div>
                            <div>
                                <p className="text-xs uppercase tracking-wider text-gray-500 font-medium">Submission Date</p>
                                <p className="mt-1 text-sm text-gray-900">{formatDate(application.submissionDate)}</p>
                            </div>
                            <div>
                                <p className="text-xs uppercase tracking-wider text-gray-500 font-medium">Leader Name</p>
                                <p className="mt-1 text-sm text-gray-900">{application.teamLeaderName || 'N/A'}</p>
                            </div>
                            <div>
                                <p className="text-xs uppercase tracking-wider text-gray-500 font-medium">Leader Email</p>
                                <p className="mt-1 text-sm text-gray-900">{application.teamLeaderEmail || 'N/A'}</p>
                            </div>
                            <div>
                                <p className="text-xs uppercase tracking-wider text-gray-500 font-medium">Leader Phone</p>
                                <p className="mt-1 text-sm text-gray-900">{application.teamLeaderPhone || 'N/A'}</p>
                            </div>
                        </div>
                    </div>

                    <div className="border-t border-gray-200 my-8" />

                    {/* Category & Availability Section */}
                    <div className="mb-8">
                        <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2 mb-4">
                            <StarIcon className="h-5 w-5 text-custom-blue-1" />
                            Category & Availability
                        </h2>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                            <div>
                                <p className="text-xs uppercase tracking-wider text-gray-500 font-medium">Category</p>
                                <p className="mt-1 text-sm text-gray-900 bg-gray-50 p-3 rounded-md">{application.category || 'N/A'}</p>
                            </div>
                            <div>
                                <p className="text-xs uppercase tracking-wider text-gray-500 font-medium">Availability</p>
                                <p className="mt-1 inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">{application.availability || 'N/A'}</p>
                            </div>
                            <div>
                                <p className="text-xs uppercase tracking-wider text-gray-500 font-medium">Time Commitment</p>
                                <p className="mt-1 text-sm text-gray-900">{application.timeCommitment ? `${application.timeCommitment} hours/week` : 'N/A'}</p>
                            </div>
                        </div>
                    </div>

                    {/* Project Demo Section */}
                    {application.projectDemoLink && (
                        <>
                            <div className="border-t border-gray-200 my-8" />
                            <div className="mb-8">
                                <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2 mb-4">
                                    <CodeIcon className="h-5 w-5 text-custom-blue-1" />
                                    Project Demo
                                </h2>
                                <div>
                                    <p className="text-xs uppercase tracking-wider text-gray-500 font-medium">Demo Link</p>
                                    <a
                                        href={application.projectDemoLink}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="mt-1 inline-flex items-center text-custom-blue-1 hover:text-blue-700 text-sm break-all"
                                    >
                                        <ExternalLinkIcon className="h-4 w-4 mr-1 flex-shrink-0" />
                                        {application.projectDemoLink}
                                    </a>
                                </div>
                            </div>
                        </>
                    )}

                    {/* Social Media Posts Section */}
                    {application.socialMediaPosts && application.socialMediaPosts.length > 0 && (
                        <>
                            <div className="border-t border-gray-200 my-8" />
                            <div className="mb-8">
                                <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2 mb-4">
                                    <StarIcon className="h-5 w-5 text-custom-blue-1" />
                                    Social Media Posts
                                </h2>
                                <div className="space-y-3">
                                    {application.socialMediaPosts.map((post, index) => (
                                        <div key={index} className="p-3 bg-gray-50 rounded-md">
                                            <p className="text-xs uppercase tracking-wider text-gray-500 font-medium">Post {index + 1}</p>
                                            <a
                                                href={post}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="mt-1 inline-flex items-center text-custom-blue-1 hover:text-blue-700 text-sm break-all"
                                            >
                                                <ExternalLinkIcon className="h-4 w-4 mr-1 flex-shrink-0" />
                                                {post}
                                            </a>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </>
                    )}

                    {/* Consent & Terms Section */}
                    <div className="border-t border-gray-200 my-8" />
                    <div>
                        <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2 mb-4">
                            <InformationCircleIcon className="h-5 w-5 text-custom-blue-1" />
                            Additional Information & Consent
                        </h2>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                                <p className="text-xs uppercase tracking-wider text-gray-500 font-medium">Referral Source</p>
                                <p className="mt-1 inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-700">{application.referralSource?.replace('-', ' ') || 'N/A'}</p>
                            </div>
                            <div>
                                <p className="text-xs uppercase tracking-wider text-gray-500 font-medium">Terms Agreement</p>
                                {application.agreeToTerms ? (
                                    <p className="mt-1 inline-flex items-center text-green-600 text-sm"><CheckCircleIcon className="h-4 w-4 mr-1" /> Agreed to terms</p>
                                ) : (
                                    <p className="mt-1 inline-flex items-center text-red-600 text-sm"><ClockIcon className="h-4 w-4 mr-1" /> Not agreed</p>
                                )}
                            </div>
                            <div>
                                <p className="text-xs uppercase tracking-wider text-gray-500 font-medium">Consent to Review</p>
                                {application.consentToReview ? (
                                    <p className="mt-1 inline-flex items-center text-green-600 text-sm"><CheckCircleIcon className="h-4 w-4 mr-1" /> Consented</p>
                                ) : (
                                    <p className="mt-1 inline-flex items-center text-red-600 text-sm"><ClockIcon className="h-4 w-4 mr-1" /> Not consented</p>
                                )}
                            </div>
                            <div>
                                <p className="text-xs uppercase tracking-wider text-gray-500 font-medium">Consent to Publicity</p>
                                {application.consentToPublicity ? (
                                    <p className="mt-1 inline-flex items-center text-green-600 text-sm"><CheckCircleIcon className="h-4 w-4 mr-1" /> Consented</p>
                                ) : (
                                    <p className="mt-1 inline-flex items-center text-red-600 text-sm"><ClockIcon className="h-4 w-4 mr-1" /> Not consented</p>
                                )}
                            </div>
                            <div className="md:col-span-2">
                                <p className="text-xs uppercase tracking-wider text-gray-500 font-medium">Commitment to Participate</p>
                                {application.commitToParticipate ? (
                                    <p className="mt-1 inline-flex items-center text-green-600 text-sm"><CheckCircleIcon className="h-4 w-4 mr-1" /> Committed to participate</p>
                                ) : (
                                    <p className="mt-1 inline-flex items-center text-red-600 text-sm"><ClockIcon className="h-4 w-4 mr-1" /> Not committed</p>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ApplicationDetails;
